import { Flex, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { ISearch } from '@/hooks/useSearchStore';
import debounce from 'lodash.debounce';
import { CloseCircleIcon, EnterIcon, SearchIcon } from '../Icons';
import { useState, KeyboardEvent, ChangeEvent, useEffect } from 'react';
import { packageSizeOptions } from '../OrderForm/PackageSizeCard';
import { humanizeKey } from '@/utils';

const { Option } = Select;

interface SearchOrderBoxProps {
  searchValue?: ISearch;
  setSearchValue: (data: ISearch) => void;
  // onClearSearchValue?: () => void;
  onSearching?: (search: ISearch) => void;
  resetPaginate?: () => void;
}

enum SearchItemEnum {
  PHONE = 'filter_phone',
  ADDRESS_TEXT = 'filter_address_text',
  FULL_NAME = 'filter_full_name',
  PACKAGE_SIZE = 'filter_size',
  PLATFORM_CODE = 'filter_platform_code',
  PICKUP_ADDRESS = 'filter_pick_up_address_title',
  CUSTOM_ID = 'filter_custom_ids[]',
}

const SearchOrderBox = ({ searchValue, setSearchValue, resetPaginate }: SearchOrderBoxProps) => {
  const [inputValue, setInputValue] = useState(searchValue?.search || '');
  const [searchByValue, setSearchByValue] = useState(searchValue?.searchBy || null);
  const { t } = useTranslation();

  const searchItems = [
    { label: 'phone_number', value: SearchItemEnum.PHONE },
    { label: 'address', value: SearchItemEnum.ADDRESS_TEXT },
    { label: 'first_name', value: SearchItemEnum.FULL_NAME },
    { label: 'package_size', value: SearchItemEnum.PACKAGE_SIZE },
    { label: 'platform_code', value: SearchItemEnum.PLATFORM_CODE },
    { label: 'pickup_address', value: SearchItemEnum.PICKUP_ADDRESS },
    { label: 'filter_custom_id', value: SearchItemEnum.CUSTOM_ID },
  ];

  // Handle select change
  const handleFilterChange = (value: string) => {
    setSearchByValue(value);
    setInputValue('');
    if (!value) handleClearSearch();
    // onClearSearchValue?.();
  };

  // Handle search input
  const handleSearchInput = () => {
    if (inputValue !== '') {
      resetPaginate?.();
      setSearchValue({ searchBy: searchByValue, search: inputValue });
      // onSearching?.({ ...searchValue!, search: inputValue });
    }
  };

  const handleEnterKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSearchInput();
    }
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      handleClearSearch();
    } else setInputValue(e.target.value);
  };

  const handleClearSearch = () => {
    setInputValue('');

    resetPaginate?.();

    setSearchValue({ searchBy: searchByValue, search: null });
    // onClearSearchValue?.();
    // onSearching?.({ ...searchValue!, search: null });
  };

  const handleChangePackageSize = (value: string) => {
    resetPaginate?.();

    setSearchValue({ searchBy: searchByValue, search: value });
    // onSearching?.({ ...searchValue!, search: value });
  };

  // Sync input value when searchValue prop changes (e.g., from URL parameters)
  useEffect(() => {
    if (searchValue?.search !== inputValue) {
      setInputValue(searchValue?.search || '');
    }
    if (searchValue?.searchBy !== searchByValue) {
      setSearchByValue(searchValue?.searchBy || '');
    }
  }, [searchValue?.search, searchValue?.searchBy]);

  // Note: Search cleanup is now handled at the page level (Orders component) in useOrdersUrlSync Hook
  // to ensure proper state management across navigation

  return (
    <div className='search-order-integrated flex items-center w-full rounded-lg border border-gray-200 bg-white overflow-hidden h-11'>
      <Select
        placeholder={t('search_by')}
        className='search-order-select h-full min-h-[42px]'
        allowClear
        size='middle'
        bordered={false}
        value={searchByValue || searchValue?.searchBy}
        onChange={handleFilterChange}
        popupMatchSelectWidth={false}
      >
        {searchItems?.map((search, index) => (
          <Option key={index} value={search?.value}>
            {t(search?.label)}
          </Option>
        ))}
      </Select>
      <div className='w-px h-6 bg-gray-200 shrink-0' />
      <div className='flex-1 min-w-0 h-full'>
        {searchByValue === SearchItemEnum.PACKAGE_SIZE ? (
          <Select
            placeholder={t('package_size')}
            className='w-full h-full'
            allowClear
            size='middle'
            bordered={false}
            value={searchValue?.search}
            onChange={handleChangePackageSize}
          >
            {packageSizeOptions?.map((size, index) => (
              <Option key={index} value={size?.key}>
                {humanizeKey(t(size?.title))}
              </Option>
            ))}
          </Select>
        ) : (
          <Input
            size='middle'
            className='w-full h-full border-none shadow-none focus:shadow-none'
            placeholder={searchByValue ? `${t('search_by')} ${t(searchItems.find(s => s.value === searchByValue)?.label || 'search')}...` : t('search')}
            prefix={<SearchIcon />}
            suffix={
              <div
                className={`flex items-center justify-end gap-2 w-12 transition-all duration-300
                ${inputValue.length >= 2 ? 'px-0' : 'overflow-hidden ps-11'} `}
              >
                <div className='w-5 flex justify-center items-center'>
                  <CloseCircleIcon
                    onClick={handleClearSearch}
                    className={`cursor-pointer p-1 mt-[1px] text-gray-400 hover:text-gray-600 transition-opacity duration-200 ${
                      inputValue.length > 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                    color='#939496'
                    width={20}
                    height={20}
                  />
                </div>

                <EnterIcon
                  width={22}
                  height={22}
                  color='#DDE4E959'
                  onClick={handleSearchInput}
                  className={`cursor-pointer transition-all duration-300 transform
        ${
          inputValue.length >= 2
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 translate-x-2 pointer-events-none'
        }`}
                />
              </div>
            }
            onChange={handleSearchInputChange}
            onKeyDown={handleEnterKey}
            value={inputValue}
          />
        )}
      </div>
    </div>
  );
};

export default SearchOrderBox;
